import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  apiRequest,
  formatCurrency,
  formatDate,
} from "../../../core/common/api";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar from "../../../shared/PaginationBar";
import ResetButton from "../../../shared/ResetButton";
import HistoryDetails from "../../../shared/HistoryDetails";
import useTableSearch from "../../../shared/useTableSearch";

const EMPTY_FILTERS = {
  fund: "all",
  type: "all",
  club: "all",
  rank: "all",
  from: "",
  to: "",
};

const HistoryList = () => {
  const [searchParams] = useSearchParams();
  const latestRequest = useRef(0);
  const [rows, setRows] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const searchTerm = useTableSearch(search);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0,
  });

  const loadRows = useCallback(
    async (page = 1) => {
      const requestId = ++latestRequest.current;
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pagination.limit),
        });
        if (searchTerm) params.set("search", searchTerm);
        if (filters.fund !== "all") params.set("fund", filters.fund);
        if (filters.type !== "all") params.set("type", filters.type);
        if (filters.club !== "all") params.set("club", filters.club);
        if (filters.rank !== "all") params.set("rank", filters.rank);
        if (filters.from) params.set("from", filters.from);
        if (filters.to) params.set("to", filters.to);

        const res = await apiRequest(`/history?${params}`, "GET");
        if (requestId !== latestRequest.current) return;
        setRows(res.history || []);
        setStats(res.stats || {});
        setPagination({
          page: res.pagination?.page || page,
          limit: res.pagination?.limit || pagination.limit,
          totalPages: res.pagination?.totalPages || 1,
          total: res.pagination?.total || 0,
        });
      } catch (error: any) {
        if (requestId !== latestRequest.current) return;
        setRows([]);
        Swal.fire("Error", error.message || "Unable to load history", "error");
      } finally {
        if (requestId === latestRequest.current) setIsLoading(false);
      }
    },
    [pagination.limit, filters, searchTerm],
  );

  useEffect(() => {
    void loadRows(1);
  }, [loadRows]);

  const applyFilters = (patch: Partial<typeof EMPTY_FILTERS> = {}) => {
    setFilters((current) => ({ ...current, ...patch }));
  };

  const resetFilters = () => {
    setSearch("");
    setFilters({ ...EMPTY_FILTERS });
  };

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">MLM</div>
          <h1 className="page-title">
            Fund <em>History</em>
          </h1>
          <div className="page-sub">
            All members. Working, Up-line, Upgrade, Re-purchases and Profit Fund
            transactions.
          </div>
        </div>
      </div>

      <section className="metrics-grid">
        <MetricCard
          metric={{
            label: "Working entries",
            value: stats.workingCount || 0,
            icon: "⚡",
            tone: "green",
            helper: formatCurrency(stats.workingAmount || 0),
          }}
        />
        <MetricCard
          metric={{
            label: "Up-line entries",
            value: stats.uplineCount || 0,
            icon: "⬆️",
            tone: "green",
            helper: formatCurrency(stats.uplineAmount || 0),
          }}
        />
        <MetricCard
          metric={{
            label: "Upgrade entries",
            value: stats.upgradeCount || 0,
            icon: "🔒",
            tone: "blue",
            helper: `${formatCurrency(stats.upgradeAmount || 0)} total transaction amount`,
          }}
        />
        <MetricCard
          metric={{
            label: "Re-purchases entries",
            value: stats.repurchaseCount || 0,
            icon: "🔁",
            tone: "orange",
            helper: `${formatCurrency(stats.repurchaseAmount || 0)} total transaction amount`,
          }}
        />
        <MetricCard
          metric={{
            label: "Profit entries",
            value: stats.profitCount || 0,
            icon: "🏦",
            tone: "green",
            helper: `${formatCurrency(stats.profitAmount || 0)} total transaction amount`,
          }}
        />
        <MetricCard
          metric={{
            label: "In this list",
            value: pagination.total,
            icon: "📄",
            tone: "blue",
          }}
        />
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Member ID or name"
          />
        </div>
        <select
          className="filter-select"
          value={filters.fund}
          onChange={(event) => applyFilters({ fund: event.target.value })}
        >
          <option value="all">All funds</option>
          <option value="working">Working</option>
          <option value="upline">Up-line</option>
          <option value="upgrade">Upgrade</option>
          <option value="repurchase">Re-purchases</option>
          <option value="profit">Profit</option>
        </select>
        <select
          className="filter-select"
          value={filters.type}
          onChange={(event) => applyFilters({ type: event.target.value })}
        >
          <option value="all">All types</option>
          <option value="credit">Credit</option>
          <option value="debit">Debit</option>
        </select>

        <label className="filter-date-wrap">
          {/* <span>From</span> */}
          <input
            type="date"
            className="filter-select filter-date"
            value={filters.from}
            onChange={(event) => applyFilters({ from: event.target.value })}
          />
        </label>
        <label className="filter-date-wrap">
          {/* <span>To</span> */}
          <input
            type="date"
            className="filter-select filter-date"
            value={filters.to}
            onChange={(event) => applyFilters({ to: event.target.value })}
          />
        </label>
        <select
          className="filter-select"
          aria-label="Club income filter"
          value={filters.club}
          onChange={(event) => applyFilters({ club: event.target.value })}
        >
          <option value="all">All clubs</option>
          {[1, 2, 3, 4, 5, 6].map(
            (club) => (
              <option key={club} value={String(club)}>
                {typeof club === "number" ? `Club ${club}` : club}
              </option>
            ),
          )}
        </select>
        <select className="filter-select" aria-label="Rank income filter" value={filters.rank}
          onChange={event => applyFilters({ rank: event.target.value })}>
          <option value="all">All ranks</option>
          {["Leader", "Director", "Partner", "Royalty"].map(rank => <option key={rank} value={rank}>{rank}</option>)}
        </select>
        <ResetButton onClick={resetFilters} />
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>History ({pagination.total})</h2>
          <p>
            Working, Up-line, Upgrade, Re-purchases and Profit credits and
            debits
          </p>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Type</th>
                <th>Fund</th>
                <th>History</th>
                <th>Amount</th>
                <th>Fund after</th>
                <th>eWallet after</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    Loading history...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    No history for these filters.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const userId = row.user?._id;
                  const memberId = row.memberId || row.user?.memberId || "—";
                  return (
                    <tr key={row._id}>
                      <td>
                        {userId ? (
                          <Link to={`/users/${userId}`}>
                            <strong>{memberId}</strong>
                          </Link>
                        ) : (
                          <strong>{memberId}</strong>
                        )}
                        <div className="table-muted">
                          {row.user?.name || "—"}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`status-badge ${
                            row.direction === "credit" ? "success" : "danger"
                          }`}
                        >
                          {row.direction === "credit" ? "Credit" : "Debit"}
                        </span>
                      </td>
                      <td>{row.fundLabel || row.fund}</td>
                      <td>
                        <HistoryDetails row={row} />
                      </td>
                      <td>
                        {row.direction === "debit" ? "−" : "+"}
                        {formatCurrency(row.amount)}
                      </td>
                      <td>{formatCurrency(row.balanceAfter)}</td>
                      <td>
                        {row.eWalletAfter == null
                          ? "—"
                          : formatCurrency(row.eWalletAfter)}
                      </td>
                      <td>{formatDate(row.createdAt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          totalPages={pagination.totalPages}
          itemLabel="entries"
          onPageChange={(page) => void loadRows(page)}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, limit, page: 1 }))
          }
        />
      </section>
    </>
  );
};

export default HistoryList;
